/** The flow designer's boxes — IOFlow's node cards (`../IOFlow/frontend/src/
    components/flow/{Trigger,Action,Condition,Loop,Response}Node.js`) ported to
    TypeScript and to this product's theme variables: the tinted card with a
    coloured border, the icon + type chip header, the label, the one-line
    detail, the id in the corner, the validation badge, IOFlow's resizer, and
    labelled output handles on the right like its condition's True / False and
    its loop's Loop Body.

    One step box serves every kind (the ports differ, not the card); Start and
    Finish are the trigger and response terminals. */

import { memo, useState } from 'react'
import { Handle, NodeResizeControl, Position } from '@xyflow/react'
import type { Node, NodeProps } from '@xyflow/react'
import type { WizardStep } from '../types'
import { KIND_META } from '../types'
import type { Category, Port } from './graph'
import { CATEGORY_TONE, NODE_W, hasNextHandle, portsOf, stepSummary } from './graph'

export interface NodeIssues { errors: string[]; warnings: string[] }

export interface StepNodeData extends Record<string, unknown> {
  step: WizardStep
  category: Category
  isStart: boolean
  issues?: NodeIssues
  project: string
  readOnly: boolean
}

export interface StartNodeData extends Record<string, unknown> {
  title: string
  mode: string
  baseObject: string
  inputs: string[]
  readOnly: boolean
}

export interface FinishNodeData extends Record<string, unknown> {
  then: string
}

export type StepFlowNode = Node<StepNodeData, 'wzStep'>
export type StartFlowNode = Node<StartNodeData, 'wzStart'>
export type FinishFlowNode = Node<FinishNodeData, 'wzFinish'>
export type WizardFlowNode = StepFlowNode | StartFlowNode | FinishFlowNode

export const PORT_TONE: Record<Port['tone'], string> = {
  next: '#94a3b8',
  case: '#f59e0b',
  else: '#d97706',
  body: '#a855f7',
  return: '#6366f1',
  issue: '#ef4444',
  saved: '#22c55e',
}

const MIN_W = 180
const MAX_W = 520

function cardStyle(tone: string, issues?: NodeIssues): React.CSSProperties {
  const border = issues?.errors.length ? 'var(--color-error)' : issues?.warnings.length ? 'var(--color-warning)' : tone
  return {
    ['--wz-tone' as string]: tone,
    position: 'relative', boxSizing: 'border-box', width: '100%', height: '100%',
    padding: '10px 12px', borderRadius: 8, border: `2px solid ${border}`,
    background: `color-mix(in srgb, ${tone} 9%, var(--color-bg-secondary))`,
    color: 'var(--color-text)',
  }
}

const idStyle: React.CSSProperties = {
  position: 'absolute', bottom: 3, right: 8, fontSize: 13, fontFamily: 'monospace',
  color: 'var(--color-text-faint, var(--color-text-secondary))',
}

function Header({ icon, label, tone, right }: { icon: string; label: string; tone: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 6 }}>
      <span className="material-icons" style={{ fontSize: 19, color: tone }}>{icon}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {right}
        <span style={{ fontSize: 13, fontWeight: 600, color: tone, whiteSpace: 'nowrap' }}>{label}</span>
      </span>
    </div>
  )
}

/** IOFlow's ValidationErrorTooltip, reduced: a badge whose hover lists the
 *  step's Check findings. */
function IssuesBadge({ issues }: { issues?: NodeIssues }) {
  const [open, setOpen] = useState(false)
  const n = (issues?.errors.length || 0) + (issues?.warnings.length || 0)
  if (!issues || !n) return null
  const err = issues.errors.length > 0
  return (
    <span className="nodrag" style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span className="material-icons" style={{ fontSize: 17, color: err ? 'var(--color-error)' : 'var(--color-warning)' }}>
        {err ? 'error' : 'warning'}
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: err ? 'var(--color-error)' : 'var(--color-warning)' }}>{n}</span>
      {open && (
        <div style={{
          position: 'absolute', top: 20, right: -8, zIndex: 50, width: 300, padding: 8,
          background: 'var(--color-raised, var(--color-bg-secondary))', border: '1px solid var(--color-border)',
          borderRadius: 6, boxShadow: '0 6px 20px var(--color-shadow, rgba(0,0,0,.3))', fontSize: 13,
          lineHeight: 1.4, color: 'var(--color-text)', textAlign: 'left', cursor: 'default',
        }}>
          {issues.errors.map((m, i) => <div key={`e${i}`} style={{ color: 'var(--color-error)', marginBottom: 4 }}>● {m}</div>)}
          {issues.warnings.map((m, i) => <div key={`w${i}`} style={{ color: 'var(--color-warning)', marginBottom: 4 }}>▲ {m}</div>)}
        </div>
      )}
    </span>
  )
}

/** IOFlow's resizer, as its stylesheet shows it: one corner handle on the
 *  selected box. Width only — a box is as tall as its routes. */
function Resizer({ selected, readOnly }: { selected: boolean; readOnly: boolean }) {
  if (!selected || readOnly) return null
  return (
    <NodeResizeControl position="bottom-right" resizeDirection="horizontal" minWidth={MIN_W} maxWidth={MAX_W}
      style={{
        width: 10, height: 10, borderRadius: 2, border: '2px solid var(--color-bg-secondary)',
        background: 'var(--color-accent)', zIndex: 11, cursor: 'ew-resize',
      }} />
  )
}

// ── a step ─────────────────────────────────────────────────────────────────

function PortRow({ port, connectable }: { port: Port; connectable: boolean }) {
  const tone = PORT_TONE[port.tone]
  return (
    <div style={{
      position: 'relative', height: 22, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      paddingRight: 6, fontSize: 13, fontWeight: 600, color: tone,
      borderTop: '1px dashed color-mix(in srgb, var(--wz-tone) 25%, transparent)',
      fontStyle: port.adds ? 'italic' : undefined,
    }} title={port.label}>
      <span style={{ maxWidth: '92%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{port.label}</span>
      <Handle type="source" position={Position.Right} id={port.id} isConnectable={connectable}
        style={{ background: tone }} />
    </div>
  )
}

export const StepNode = memo(function StepNode({ id, data, selected }: NodeProps<StepFlowNode>) {
  const { step, category, isStart, issues, project, readOnly } = data
  const tone = CATEGORY_TONE[category]
  const meta = KIND_META[step.kind]
  const ports = portsOf(step)
  const child = (step.kind === 'call' || step.kind === 'handoff') && step.wizard ? step.wizard : null
  const connectable = !readOnly
  return (
    <>
      <Resizer selected={!!selected} readOnly={readOnly} />
      <div style={{ ...cardStyle(tone, issues), padding: 0, display: 'flex', flexDirection: 'column' }}
        data-tw-wizard-flow-step={id}>
        <Handle type="target" position={Position.Top} id="in" isConnectable={connectable} />
        <Handle type="target" position={Position.Left} id="in-left" isConnectable={connectable} />
        <div style={{ padding: '10px 12px 18px', position: 'relative', flex: '1 0 auto' }}>
          <Header icon={meta?.icon || 'circle'} label={meta?.label || step.kind} tone={tone}
            right={<>
              {isStart && <span style={{ fontSize: 13, fontWeight: 700, color: CATEGORY_TONE.start }}>START</span>}
              {!meta?.screen && <span title="Runs with no screen" className="material-icons" style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>visibility_off</span>}
              <IssuesBadge issues={issues} />
            </>} />
          <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {step.title || meta?.label || step.id}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {child ? (
              <>
                {step.kind === 'call' ? 'call ' : 'hand off to '}
                <a className="nodrag" href={`/designers/wizard/${encodeURIComponent(project)}/${encodeURIComponent(child)}`}
                  target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--color-accent)' }}>{child} ↗</a>
              </>
            ) : stepSummary(step)}
          </div>
          {step.when && (
            <div style={{ fontSize: 13, marginTop: 2, color: CATEGORY_TONE.branch, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={`Runs only when ${step.when}`}>
              when {step.when}
            </div>
          )}
          <div style={idStyle}>{id}</div>
        </div>
        {ports.length > 0 && (
          <div style={{ paddingLeft: 12 }}>
            {ports.map((p) => <PortRow key={p.id} port={p} connectable={connectable} />)}
          </div>
        )}
        {hasNextHandle(step) && (
          <Handle type="source" position={Position.Bottom} id="next" isConnectable={connectable} />
        )}
        {/* Read-only routes (an op / tool / pick-row goto) leave from here. */}
        <Handle type="source" position={Position.Right} id="aux" isConnectable={false}
          style={{ top: 'auto', bottom: 6, opacity: 0, pointerEvents: 'none' }} />
      </div>
    </>
  )
})

// ── Start (IOFlow's trigger) ───────────────────────────────────────────────

export const StartNode = memo(function StartNode({ data, selected }: NodeProps<StartFlowNode>) {
  const tone = CATEGORY_TONE.start
  return (
    <>
      <Resizer selected={!!selected} readOnly={data.readOnly} />
      <div style={cardStyle(tone)} data-tw-wizard-flow-step="__start">
        <Header icon="play_circle" label={`Start · ${data.mode.replace(/_/g, ' ')}`} tone={tone} />
        <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.title || 'Wizard'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
          {data.baseObject ? `on ${data.baseObject}` : 'no base transaction yet'}
        </div>
        {data.inputs.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {data.inputs.slice(0, 4).map((n) => (
              <span key={n} style={{
                fontSize: 13, fontFamily: 'monospace', padding: '0 4px', borderRadius: 3,
                color: 'var(--color-accent)', background: 'var(--color-accent-weak)',
              }}>input.{n}</span>
            ))}
            {data.inputs.length > 4 && <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>+{data.inputs.length - 4} more</span>}
          </div>
        )}
        <Handle type="source" position={Position.Bottom} id="start" isConnectable={!data.readOnly} />
      </div>
    </>
  )
})

// ── Finish (IOFlow's response) ─────────────────────────────────────────────

const FINISH_THEN: Record<string, string> = {
  restart: 'then starts again',
  close: 'then closes',
  open_record: 'then opens the record',
}

export const FinishNode = memo(function FinishNode({ data }: NodeProps<FinishFlowNode>) {
  const tone = CATEGORY_TONE.finish
  return (
    <div style={cardStyle(tone)} data-tw-wizard-flow-step="__finish">
      <Handle type="target" position={Position.Top} id="in" />
      <Handle type="target" position={Position.Left} id="in-left" />
      <Header icon="flag" label="Finish" tone={tone} />
      <div style={{ fontWeight: 600, fontSize: 14 }}>Finish</div>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
        {FINISH_THEN[data.then] || (data.then ? `then ${data.then}` : 'the wizard ends')}
      </div>
    </div>
  )
})

export const nodeTypes = { wzStep: StepNode, wzStart: StartNode, wzFinish: FinishNode }

export { NODE_W }
